import React, { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import styles from "./TestUploadComponent.module.css";
import FileUploadBox from "@components/fileuploadbox/FileUploadBox";
import { getUploadURL } from "@services/testCreation";
import { uploadFileToS3 } from "@services/answersheetUpload";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

interface TestUploadPageProps {
    onAllUploadsCompleted?: (isCompleted: boolean) => void;
    onContinue?: (questionObject:any) => void;
    showContinueButton?: boolean;
    onClose?: () => void;
}

const TestUploadPage = ({onAllUploadsCompleted, 
    onContinue, 
    showContinueButton = false ,
    onClose
  }:TestUploadPageProps) => {
  const [questionPaperStatus, setQuestionPaperStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [rubricsStatus, setRubricsStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [solutionStatus, setSolutionStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  const [questionPaperFile, setQuestionPaperFile] = useState<{ name: string; size: string } | null>(null);
  const [rubricsFile, setRubricsFile] = useState<{ name: string; size: string } | null>(null);
  const [solutionFile, setSolutionFile] = useState<{ name: string; size: string } | null>(null);

  // UUID-based fileKeys to send in API payload
  const [questionFileKey, setQuestionFileKey] = useState<string | null>(null);
  const [rubricFileKey, setRubricFileKey] = useState<string | null>(null);
  const [solutionFileKey, setSolutionFileKey] = useState<string | null>(null);

  const [minMarks, setMinMarks] = useState<string>("");
  const [maxMarks, setMaxMarks] = useState<string>("");
  const [marksError, setMarksError] = useState<string | null>(null);


  const allCompleted =
    questionPaperStatus === "completed" &&
    rubricsStatus === "completed" &&
    solutionStatus === "completed" && minMarks !== "" && maxMarks !== "" && !marksError;

  useEffect(() => {
    onAllUploadsCompleted?.(allCompleted);
  }, [allCompleted, onAllUploadsCompleted]);

function generateFileKey(file: File) {
  const ext = file.name.split(".").pop();
  return `${uuidv4()}.${ext}`;
}

const handleUpload = async (
    event: ChangeEvent<HTMLInputElement>,
    setStatus: React.Dispatch<React.SetStateAction<"idle" | "uploading" | "completed" | "error">>,
    setFileData: React.Dispatch<React.SetStateAction<{ name: string; size: string } | null>>,
    setFileKey: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setStatus("uploading");
    try {
      const fileKey = generateFileKey(file); // ← generate UUID filename!!
      const uploadURL = await getUploadURL({ filename: fileKey });
      if(uploadURL){await uploadFileToS3(file, uploadURL);}
      setFileKey(fileKey); // ← save for your questions[] array
      setFileData({
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
      });
      setStatus("completed");
    } catch (err) {
      setStatus("error");
    }
  };


  const handleRemove = (
    setStatus: React.Dispatch<
      React.SetStateAction<"idle" | "uploading" | "completed" | "error">
    >,
    setFileData: React.Dispatch<
      React.SetStateAction<{ name: string; size: string } | null>
    >,
    setFileKey: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    setStatus("idle");
    setFileData(null);
    setFileKey(null);
  };

  const validateMarks = (type: "min" | "max") => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
        const newMin = type === "min" ? value : minMarks;
      const newMax = type === "max" ? value : maxMarks;
  
      setMinMarks(newMin);
      setMaxMarks(newMax);
  
      if (value === "") {
        setMarksError("Marks cannot be empty");
        return;
      }
      const min = Number(newMin);
      const max = Number(newMax);
  
      if (isNaN(min) || isNaN(max) || max < 0) {
        setMarksError("Please enter a valid non-negative number");
        return;
      }
        if (newMin !== "" && newMax !== "") {
        if (min >= max) {
          setMarksError("Minimum marks must be LESS than maximum marks");
          return;
        }
      }
        setMarksError(null);
    };
  };
  
    

  const questionsPayload = [
    {
      min_marks: minMarks,
      max_marks: maxMarks,
      question_document: {
        filename: questionFileKey,
      },
      rubric_document: {
        filename: rubricFileKey,
      },
      sample_answers: [
        {
          document: {
            filename: solutionFileKey,
          },
        },
      ],
    },
  ];

  return (
    <div className={styles.wrapper}>
    <div className={styles.closeIcon} onClick={onClose}>X</div>
    <div className={styles.container}>
        <div className={styles.marksInputsContainer}>
        <label className={styles.marksLabel}>Minimum marks :</label>
        <input
          type="text"
          className={styles.marksInput}
          value={minMarks}
          onChange={validateMarks("min")}
        />
        <label className={styles.marksLabel}>Maximum marks :</label>
        <input
          type="text"
          className={styles.marksInput}
          value={maxMarks}
          onChange={validateMarks("max")}
        />
        </div>
        {marksError && <div style={{ color: "red" }}>{marksError}</div>}
        </div>
      <div className={styles.uploadsContainer}>
        <FileUploadBox
          label="QUESTION PAPER"
          uploadState={questionPaperStatus}
          fileName={questionPaperFile?.name}
          fileSize={questionPaperFile?.size}
          onFileChange={(e) =>
            handleUpload(
              e,
              setQuestionPaperStatus,
              setQuestionPaperFile,
              setQuestionFileKey
            )
          }
          onRemove={() =>
            handleRemove(
              setQuestionPaperStatus,
              setQuestionPaperFile,
              setQuestionFileKey
            )
          }
        />

        <FileUploadBox
          label="RUBRICS"
          uploadState={rubricsStatus}
          fileName={rubricsFile?.name}
          fileSize={rubricsFile?.size}
          onFileChange={(e) =>
            handleUpload(
              e,
              setRubricsStatus,
              setRubricsFile,
              setRubricFileKey
            )
          }
          onRemove={() =>
            handleRemove(
              setRubricsStatus,
              setRubricsFile,
              setRubricFileKey
            )
          }
        />

        <FileUploadBox
          label="SOLUTION"
          uploadState={solutionStatus}
          fileName={solutionFile?.name}
          fileSize={solutionFile?.size}
          onFileChange={(e) =>
            handleUpload(
              e,
              setSolutionStatus,
              setSolutionFile,
              setSolutionFileKey
            )
          }
          onRemove={() =>
            handleRemove(
              setSolutionStatus,
              setSolutionFile,
              setSolutionFileKey
            )
          }
        />
      </div>

    {showContinueButton && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            // onClick={onContinue}
            onClick={() => {
                if (allCompleted) {
                  onContinue?.(questionsPayload[0]); // <-- send question JSON to parent
                }
              }}
            disabled={!allCompleted}
            style={{
              padding: '12px 24px',
              backgroundColor: allCompleted ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: allCompleted ? 'pointer' : 'not-allowed',
              fontSize: '16px'
            }}
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default TestUploadPage;